/**
 * earth - a project to visualize global weather data.
 *
 * Copyright (c) 2020 Cameron Beccario
 *
 * For a free version of this project, see https://github.com/cambecc/earth
 */

import {rescale} from "./util/math.js";
import * as _ from "./lib/underscore.js";
import {createAgent} from "./framework/agent.js";
import * as d3 from "./lib/d3.js";
import {newClickEvent} from "./util/micro.js";
import {isDebugContext, isIE11, isIFrameContext, isIOS, isKioskContext, siteInstance} from "./util/context.js";
import {defer} from "./util/functions.js";
import {isSubset, ø} from "./util/objects.js";
import {toArray} from "./util/arrays.js";
import {seq} from "./util/seq.js";
import {gtagEvent} from "./util/gtag.js";
import * as utc from "./util/utc.js";
import {overlayTypes} from "./product/products.js";
import {computeViewbox, defineViewbox} from "./ux/viewbox.js";
import {handleAsChord} from "./ux/keyboard.js";
import * as calendar from "./ux/calendar.js";
import * as twod from "./canvas/twod.js";
import * as language from "./ux/lang.js";
import * as glTest from "./gl/glTest.js";
import {buildInputController} from "./ux/inputController.js";
import {buildReporter} from "./ux/reporter.js";
import * as globes from "./engine/globes.js";
import {buildMesh} from "./engine/mesh.js";
import {buildRenderer} from "./engine/renderer.js";
import {buildLayers, navigate} from "./engine/layers.js";
import {interpolateField} from "./engine/field.js";
import {animateParticles} from "./engine/animate.js";
import {drawOverlay} from "./engine/overlay.js";
import {buildFastoverlay} from "./gl/fastoverlay.js";

import {attach as sharedAttach, model as sharedConfig} from "./framework/sharedState.js";
import {attach as sessionAttach, model as sessionConfig} from "./framework/sessionState.js";
import {formatAsDecimalDegrees} from "./util/strings.js";
import {createPrimaryWavesLayer} from "./product/ww3/ww3.js";
import {debounce} from "./lib/underscore.js";
// import {renderArgoBar} from "./profileBar.js";
// import {argoProfilePath} from "./product/argo.js";

export function earth(app) {

    const {bridge} = app;

    console.log("start...");

    const report = buildReporter(bridge);

    function newAgent(name, options) {
        return createAgent(ø(options, {name})).on(seq`reject.? fail.?`, report.error);
    }

    // Construct the page's main internal components:

    const viewboxAgent = newAgent("viewbox", {initial: computeViewbox()});  // the dimensions of the display
    const inputController = buildInputController(viewboxAgent);    // interprets drag/zoom operations
    const meshAgent = newAgent("mesh");                // map data for the earth
    const globeAgent = newAgent("globe");              // the model of the globe
    const layerManagerAgent = newAgent("layer");       // the layers of weather data
    const primaryLayerAgent = newAgent("primaryLayer");
    const overlayLayerAgent = newAgent("overlayLayer");
    const activeLayerAgent = newAgent("activeLayer");
    const rendererAgent = newAgent(                    // the globe SVG renderer
        "renderer",
        {events: ["start", "render", "redraw"]},
    );
    // const argoAgent = newAgent("argo");             // SVG argo maker
    const fieldAgent = newAgent("field");              // the interpolated wind vector field
    const fastoverlayAgent = newAgent("fastoverlay");  // the interpolated wind vector field
    const animatorAgent = newAgent("animator");        // the wind animator
    const overlayAgent = newAgent("overlay");          // color overlay over the animation
    const overlayGridAgent = newAgent("overlayGrid");  // grid points drawn on the overlay
    const buttonStateNotifier = newAgent("button");    // notifies consumers of button state changes

    d3.select("#show-location").on(seq`click.?`, function() {
        if (navigator.geolocation) {
            report.status("Finding current position...");
            navigator.geolocation.getCurrentPosition(function(pos) {
                report.status("");
                moveSpotlightTo(pos.coords.longitude, pos.coords.latitude);
            }, report.error);
        }
    });

    function orient(coord) {
        const globe = globeAgent.value();
        if (globe) {
            const rotate = globe.locate(coord);
            if (rotate) {
                globe.projection.rotate(rotate);
                sharedConfig.save({orientation: globe.getOrientation()});  // triggers reorientation
            }
        }
    }

    /**
     * @param {String} projection_type the desired projection
     * @returns {Object} a globe object.
     */
    function buildGlobe(projection_type) {
        const builder = globes[projection_type];
        if (!builder) {
            return Promise.reject("Unknown projection: " + projection_type);
        }
        const viewbox = viewboxAgent.value();
        const globe = builder(viewbox);
        globe.setOrientation(sharedConfig.get("orientation"), viewbox);
        return globe;
    }

/*
    function addArgo() {
        const globe =  globeAgent.value();
        if (!globe) return null;

        const svg = d3.select("#annotation");
        const type = sharedConfig.get("argoFloat");
        if (type !== "recent") {
            svg.selectAll(".argo").remove();
            return;
        }
        const cancel = this.cancel;
        const time_cursor = sharedConfig.get("time_cursor");
        const profileFile = argoProfilePath(time_cursor);
        const files = [loadJson(profileFile)];
        return Promise.all(files).then(([profiles]) => {
            if (cancel.requested) return null;

            console.time("building argo");

            svg.selectAll(".argo").remove();

            const validTypes = ["recent", "operational", "planned", "dead"];
            if (_.indexOf(validTypes, type) >= 0) {
                const path = d3.geoPath().projection(globe.projection).pointRadius(4);
                const points = profiles.map(e => {
                    return {
                        type: "Point",
                        coordinates: [e.lon, e.lat],
                        size: 4,
                        profile: e,
                    };
                });
                svg.append("g")
                    .classed("argo", true)
                    .selectAll("path")
                    .data(points)
                    .join("path")
                        .classed("argo argo-" + type, true)
                        .attr("d", path)
                    .on(seq`click.?`, function() {
                        const e = d3.select(this);
                        e.classed("argo-planned", true);
                        const {profile} = e.datum();
                        renderArgoBar(profile);
                    });
            }

            console.timeEnd("building argo");
        });
    }
*/

    /**
     * Get the overlay's valid time.
     */
    function activeValidTime() {
        // Use the active layer's validTime, otherwise use the time_cursor.
        const activeLayer = activeLayerAgent.value();
        const validTime = activeLayer?.validTime() ?? sharedConfig.get("time_cursor");
        const format = activeLayer?.dateFormat() ?? "{YYYY}-{MM}-{DD} {hh}:{mm}";
        return {validTime: validTime === "current" ? undefined : validTime, format};
    }

    /**
     * Display the overlay's valid time in the menu. Allow toggling between local and UTC time.
     */
    function showActiveValidTime() {
        const {validTime, format} = activeValidTime();
        const isLocal = d3.select("#data-date").classed("local");
        let formatted = "";
        if (validTime) {
            formatted = utc.print((isLocal ? utc.localParts : utc.parts)(utc.date(validTime)), format);
            calendar.update(validTime);
        }
        d3.select("#data-date").text(formatted + " " + (isLocal ? "Local" : "UTC"));
        d3.select("#swap-tz").text("⇄ " + (isLocal ? "UTC" : "Local"));

        // TIME-LAPSE
        // const div = d3.select("#tara-stats");
        // div.classed("hidden", false).selectAll("*").remove();
        // div.append("p").text(utc.print(validTime, "{YYYY}-{MM}-{DD} {hh}:{mm}Z"));

        return validTime;
    }

    /**
     * Display the layer types in the menu.
     */
    function showLayerDetails() {
        const primaryLayer = primaryLayerAgent.value();
        const activeLayer = activeLayerAgent.value();

        const validTime = showActiveValidTime();
        const sourceHTML = activeLayer?.sourceHTML ?? "";

        const primaryName = primaryLayer?.descriptionHTML.name ?? "";
        const primaryQual = primaryLayer?.descriptionHTML.qualifier ?? "";
        const activeName = activeLayer?.descriptionHTML.name ?? "";
        const activeQual = activeLayer?.descriptionHTML.qualifier ?? "";

        let descriptionHTML = activeName + activeQual;
        if (descriptionHTML && primaryLayer !== activeLayer) {
            // Combine both layer descriptions together with a " + " if their qualifiers are the same.
            if (primaryQual === activeQual) {
                descriptionHTML = `${primaryName} + ${descriptionHTML}`;
            } else {
                descriptionHTML = `${primaryName + primaryQual} + ${descriptionHTML}`;
            }
        }

        d3.select("#data-layer").html(descriptionHTML);
        d3.select("#data-center").html(sourceHTML);
        bridge.productChange?.(utc.date(validTime).getTime(), descriptionHTML, sourceHTML);
    }
    const showLayerDetails_debounced = debounce(showLayerDetails, 1);

    /**
     * Constructs a toggler for the specified layer's units, storing the toggle state on the element having
     * the specified id. For example, given a layer having units ["m/s", "mph"], the object returned by this
     * method sets the element's "data-index" attribute to 0 for m/s and 1 for mph. Calling value() returns the
     * currently active units object. Calling next() increments the index.
     */
    function createUnitToggle(id, layer) {
        const {type} = layer;
        const descriptors = Object.values(layer.unitDescriptors);

        function currentState() {
            return JSON.parse(d3.select(id).attr("data-index") || "{}");
        }
        function currentIndex(state) {
            return +state[type] || 0;
        }

        return {
            value: function() {
                return descriptors[currentIndex(currentState())];
            },
            next: function() {
                const state = currentState();
                state[type] = (currentIndex(state) + 1) % descriptors.length;
                d3.select(id).attr("data-index", JSON.stringify(state));
            }
        };
    }

    /**
     * Display the specified vector value. Allow toggling between the different types of units.
     */
    function showPrimarySpotlight(wind, layer) {
        const unitToggle = createUnitToggle("#location-wind-units", layer);
        const units = unitToggle.value();
        const formattedVal = units.format(wind).formattedVal ?? "";
        if (formattedVal) {
            d3.select("#location-wind").text(formattedVal);
            d3.select("#location-wind-units").html(units.html).on("click.toggle-wind-units", function() {
                unitToggle.next();
                showPrimarySpotlight(wind, layer);
            });
        }
    }

    /**
     * Display the specified overlay value. Allow toggling between the different types of supported units.
     */
    function showOverlaySpotlight(value, layer) {
        const unitToggle = createUnitToggle("#location-value-units", layer);
        const units = unitToggle.value();
        const formattedVal = units.format(value).formattedVal ?? "";
        if (formattedVal) {
            d3.select("#location-value").text(formattedVal);
            d3.select("#location-value-units").html(units.html).on("click.toggle-value-units", function() {
                unitToggle.next();
                showOverlaySpotlight(value, layer);
            });
        }
    }

    /**
     * Display a local data spotlight at the coordinates specified by the hash fragment "loc=lon,lat". The location may
     * not be valid, in which case no spotlight is displayed. Display data for both the primary layer and overlay layer,
     * performing interpolation when necessary.
     */
    function updateSpotlight() {
        d3.select("#location").classed("hidden", true);
        d3.selectAll(".location").text("");
        d3.select("#location-close").classed("hidden", true);

        const [λ, φ] = sharedConfig.get("spotlight_coords") ?? [NaN, NaN];
        if (λ !== λ || φ !== φ) {
            d3.select(".location-mark").remove();
            bridge.showSpotlightDetails?.("", "{}");
            return;
        }

        const globe = globeAgent.value();
        if (!globe) return;

        const primary = primaryLayerAgent.value();
        const overlay = overlayLayerAgent.value();
        const units = {};
        const coordString = formatAsDecimalDegrees([λ, φ]) ?? "";

        // Show coordinates.
        d3.select("#location").classed("hidden", false);
        d3.select("#location-coord").text(coordString);
        d3.select("#location-close").classed("hidden", false);

        function showPrimary(value, layer) {
            if (value === undefined) {
                return;
            }
            showPrimarySpotlight(value, layer);
            units[layer.type] = _.object(Object.values(layer.unitDescriptors).map(unit => {
                return [unit.symbol, unit.format(value).formattedVal];
            }));
        }

        function showOverlay(value, layer) {
            if (value === undefined) {
                return;
            }
            showOverlaySpotlight(value, layer);
            units[layer.type] = _.object(Object.values(layer.unitDescriptors).map(unit => {
                return [unit.symbol, unit.format(value).formattedVal];
            }));
        }

        if (primary) {
            showPrimary(primary.valueAt?.(λ, φ), primary);
        }
        if (overlay) {
            showOverlay(overlay.valueAt?.(λ, φ), overlay);
        }

        // Draw spotlight marker.
        const path = d3.geoPath().projection(globe.projection).pointRadius(7);
        let mark = d3.select(".location-mark");
        if (!mark.node()) {
            mark = d3.select("#annotation").append("path").attr("class", "location-mark");
        }
        mark.datum({type: "Point", coordinates: [λ, φ]}).attr("d", path);

        bridge.showSpotlightDetails?.(coordString, JSON.stringify(units));
    }
    const updateSpotlight_debounced = debounce(updateSpotlight, 1);

    function saveSpotlight(point, coord) {
        const λ = +coord[0], φ = +coord[1];
        const field = fieldAgent.value();
        if (point && field && !field.isInsideBoundary(point[0], point[1])) {
            return;
        }
        if (λ === λ && φ === φ) {
            sharedConfig.save({spotlight_coords: [λ, φ]});
        }
    }

    function removeSpotlight() {
        sharedConfig.save({spotlight_coords: undefined});
    }

    function moveSpotlightTo(lon, lat) {
        const coord = [+lon, +lat];
        const [λ, φ] = coord;
        if (λ !== λ || φ !== φ) {
            removeSpotlight();
            return;
        }
        saveSpotlight(null, coord);
        orient(coord);
    }

    function stopCurrentAnimation(alsoClearCanvas) {
        if (animatorAgent.value()) {
            animatorAgent.submit(false);
        }
        if (alsoClearCanvas) {
            twod.clearCanvas(d3.select("#animation").node());
        }
    }

    function highlightWhen(elementId, highlightAttrList) {
        // CONSIDER: the reasons requiring highlightAttrList need to be rethought...
        sharedConfig.on(seq`change.?`, () => {
            const attr = sharedConfig.getAll();
            let highlighted = false;
            highlightAttrList.forEach(expectedAttr => highlighted |= isSubset(attr, expectedAttr));
            d3.select(elementId).classed("highlighted", highlighted);
        });
    }

    /**
     * Registers a click event handler for the specified DOM element which modifies the configuration to have
     * the attributes represented by newAttr. An event listener is also registered for configuration change events,
     * so when a change occurs the button becomes highlighted (i.e., class ".highlighted" is assigned or removed) if
     * the configuration matches the attributes for this button. The set of attributes used for the matching is taken
     * from newAttr, unless a custom set of key+values is provided.
     */
    function bindButtonToConfiguration(elementId, newAttr, highlightAttrList) {
        highlightAttrList = highlightAttrList || [newAttr];
        d3.select(elementId).on(seq`click.?`, function() {
            if (d3.select(elementId).classed("disabled")) return;
            sharedConfig.save(newAttr);
        });
        highlightWhen(elementId, highlightAttrList);
    }

    function defineScreenFiller(viewboxAgent) {

        function updateDOM() {
            const {width, height} = viewboxAgent.value();
            const ratio = window.devicePixelRatio ?? 1;
            d3.selectAll(".fill-screen")
                .attr("width", width)
                .attr("height", height);
            d3.selectAll(".max-screen")
                .style("width", `${width}px`)
                .style("height", `${height}px`)
                .attr("width", width * ratio)
                .attr("height", height * ratio)
                .nodes()
                .forEach(node => node.getContext("2d").scale(ratio, ratio));
            d3.select("#fastoverlay")
                .style("width", `${width}px`)
                .style("height", `${height}px`);
        }

        updateDOM();
        viewboxAgent.on(seq`update.?`, updateDOM);
    }

    function shouldPlayAnimation(nextMode) {
        const currentMode = sharedConfig.get("mode");
        if (currentMode !== "space" && nextMode === "space") {
            return false;
        } else if (currentMode === "space" && nextMode !== "space") {
            return true;
        } else {
            return sharedConfig.get("animation_enabled");  // current setting
        }
    }

    /**
     * Registers all event handlers to bind components and page elements together. There must be a cleaner
     * way to accomplish this...
     */
    function init() {
        report.status("Initializing...");

        defineViewbox(viewboxAgent);
        defineScreenFiller(viewboxAgent);

        d3.select("#sponsor-link")
            .attr("target", isIFrameContext() && siteInstance() !== "tara" ? "_new" : null);
        d3.select("#sponsor-hide").on(seq`click.?`, function() {
            d3.select("#sponsor").classed("hidden", true);
        });
        d3.select("#notice-hide").on(seq`click.?`, function() {
            d3.select("#notice").classed("hidden", true);
        });
        d3.select("#toggle-settings").on(seq`click.?`, function() {
            const opening = d3.select("#settings-wrap").classed("hidden");
            d3.select("#settings-wrap").classed("hidden", !opening);
            if (opening && !d3.select("#menu").classed("collapsed")) {  // hide menu if open
                doClick("#toggle-menu");
            }
        });
        d3.select("#settings-hide").on(seq`click.?`, function() {
            d3.select("#settings-wrap").classed("hidden", true);
        });
        d3.select("#toggle-calendar").on(seq`click.?`, function() {
            const opening = d3.select("#calendar-wrapper").classed("hidden");
            d3.select("#calendar-wrapper").classed("hidden", !opening);
            if (opening && !d3.select("#menu").classed("collapsed")) {  // hide menu if open
                doClick("toggle-menu");
            }
        });
        d3.select("#calendar-hide").on(seq`click.?`, function() {
            d3.select("#calendar-wrapper").classed("hidden", true);
        });
        !function() {

            // highlight active lang
            const lang = d3.select("html").attr("lang");
            d3.select("#settings").selectAll("a[lang='" + lang + "']").classed("highlighted", true);

            // show preferred lang
            const best = language.best(window.navigator);
            if (best !== lang) {
                d3.select("#tools").select("a[lang='" + best + "']").classed("hidden", false);
            }
        }();

        // Adjust size of the scale canvas to fill the width of the menu to the right of the label.
        const label = d3.select("#scale-label").node();
        d3.select("#scale")
            .attr("width", Math.round((d3.select("#menu").node().offsetWidth - label.offsetWidth) * 0.90))
            .attr("height", Math.round(label.offsetHeight / 2));

        d3.select("#toggle-menu").on(seq`click.?`, function() {
            if (isIFrameContext() && !isKioskContext() && siteInstance() !== "tara") {
                window.open("https://earth.nullschool.net/" + window.location.hash, "_blank");
            }
            else {
                const opening = d3.select("#menu").classed("collapsed");
                d3.select("#menu").classed("collapsed", !opening);
            }
        });

        // Tweak document to distinguish CSS styling between touch and non-touch environments. Hacky hack.
        if ("ontouchstart" in document.documentElement) {
            d3.select(document).on(seq`touchstart.?`, function() {});  // this hack enables :active pseudoclass
        } else {
            d3.select(document.documentElement).classed("no-touch", true);  // to filter styles problematic for touch
        }

        function updateLangLinks() {
            function update() {
                const e = d3.select(this);
                const link = e.attr("data-link");
                return link ? `${link}${isKioskContext() ? "?kiosk" : ""}${window.location.hash}` : e.attr("href");
            }

            // Change all lang links to retain the current hash fragment so stuff stays the same when clicked.
            d3.selectAll("a[lang]").attr("href", update);
            d3.selectAll("a.dyna-link").attr("href", update);
        }

        updateLangLinks();

        // Bind lang links URL bar changes.
        d3.select(window).on(seq`hashchange.?`, function() {
            console.log("hashchange");
            updateLangLinks();
        });

        sharedConfig.on(seq`change.?`, function() {
            report.reset();
            buttonStateNotifier.submit(function() {  // use agent to delay notification until button modification is done
                const highlighted = [], disabled = [];
                d3.selectAll(".highlighted").each(function() { highlighted.push(this.id); });
                d3.selectAll(".disabled").each(function() { disabled.push(this.id); });
                bridge.buttonStateChange?.(JSON.stringify(highlighted), JSON.stringify(disabled));
            });
        });

        sessionConfig.on(seq`change:topology.?`, url => {
            meshAgent.submit(function() { return buildMesh(url, report, this.cancel); });
        });

        sessionConfig.on(seq`change:hd_enabled.?`, () => {
            layerManagerAgent.submit(function() {
                return buildLayers(
                    layerManagerAgent,
                    primaryLayerAgent,
                    overlayLayerAgent,
                    activeLayerAgent,
                    report,
                    this.cancel
                );
            });
        });

        sharedConfig.on(seq`change:projection_type.?`, projection_type => {
            globeAgent.submit(buildGlobe, projection_type);
        });
        viewboxAgent.on(seq`update.?`, () => {
            globeAgent.submit(buildGlobe, sharedConfig.get("projection_type"));
        });

        sharedConfig.on(seq`change.?`, delta => {
            const changed = Object.keys(delta);
            let rebuildRequired = false;

            // Build a new primary layer if any related attributes have changed.
            if (_.intersection(changed, ["time_cursor", "animation_type", "z_level"]).length > 0) {
                rebuildRequired = true;
            }
            // Build a new overlay layer if the new overlay type is different from the current one.
            const overlay_type = sharedConfig.get("overlay_type");
            if (changed.indexOf("overlay_type") >= 0 && overlay_type !== "none") {
                const overlay = overlayLayerAgent.value();
                if (!overlay) {
                    // Do a rebuild if we have no overlay layer.
                    rebuildRequired = true;
                } else if (overlay.type !== overlay_type) {
                    // Do a rebuild if the types are different.
                    rebuildRequired = true;
                }
            }

            if (rebuildRequired) {
                layerManagerAgent.submit(function() {
                    return buildLayers(
                        layerManagerAgent,
                        primaryLayerAgent,
                        overlayLayerAgent,
                        activeLayerAgent,
                        report,
                        this.cancel
                    );
                });
            }
        });
        primaryLayerAgent.on(seq`update.?`, showLayerDetails_debounced);
        overlayLayerAgent.on(seq`update.?`, showLayerDetails_debounced);
        activeLayerAgent.on(seq`update.?`, showLayerDetails_debounced);
        d3.select("#swap-tz").on(seq`click.?`, function() {
            d3.select("#data-date").classed("local", !d3.select("#data-date").classed("local"));
            showActiveValidTime();
        });

        function startRendering() {
            rendererAgent.submit(() => buildRenderer(
                rendererAgent,
                inputController,
                meshAgent.value(),
                globeAgent.value(),
                report,
                updateSpotlight_debounced,
                orient,
                viewboxAgent,
            ));
        }
        meshAgent.on(seq`update.?`, startRendering);
        globeAgent.on(seq`update.?`, startRendering);

        function startInterpolation() {
            fieldAgent.submit(function() {
                return interpolateField(
                    globeAgent.value(),
                    primaryLayerAgent,
                    activeLayerAgent,
                    rendererAgent,
                    fastoverlayAgent,
                    viewboxAgent,
                    report,
                    this.cancel,
                );
            });
        }

        function cancelInterpolation() {
            fieldAgent.cancel();
            overlayGridAgent.cancel();
        }

        primaryLayerAgent.on(seq`update.?`, startInterpolation);
        activeLayerAgent.on(seq`update.?`, startInterpolation);
        rendererAgent.on(seq`render.?`, startInterpolation);
        rendererAgent.on(seq`start.? redraw.?`, cancelInterpolation);

/*
        globeAgent.on(seq`update.?`, () => {
           argoAgent.submit(addArgo);
        });
        rendererAgent.on(seq`render.?`, () => {
           argoAgent.submit(addArgo);
        });
        sharedConfig.on(seq`change:time_cursor.?`, () => {
           argoAgent.submit(addArgo);
        });
        sharedConfig.on(seq`change:argoFloat.?`, () => {
           argoAgent.submit(addArgo);
        });
*/

        fieldAgent.on(seq`update.?`, () => {
            animatorAgent.submit(function() {
                return animateParticles(
                    globeAgent.value(),
                    primaryLayerAgent.value(),
                    fieldAgent.value(),
                    viewboxAgent,
                    this.cancel,
                );
            });
        });
        rendererAgent.on(seq`start.?`, () => stopCurrentAnimation(true));
        fieldAgent.on(seq`submit.?`, () => stopCurrentAnimation(false));

        function submitDrawOverlay(overlay_type, animation_enabled) {
            overlayAgent.submit(() => drawOverlay(
                overlayGridAgent,
                rendererAgent,
                activeLayerAgent,
                globeAgent,
                viewboxAgent,
                fieldAgent.value(),
                overlay_type,
                animation_enabled,
                createUnitToggle,
            ));
        }

        fieldAgent.on(seq`update.?`, () => {
            submitDrawOverlay(sharedConfig.get("overlay_type"), sharedConfig.get("animation_enabled"));
        });
        rendererAgent.on(seq`start.?`, () => {
            // Immediately clear overlay. Chrome will wait until mouse pauses before agent is triggered. :(
            twod.clearCanvas(d3.select("#overlay").node());
            submitDrawOverlay(null, null);
        });
        sharedConfig.on(seq`change.?`, delta => {
            if ("overlay_type" in delta || "animation_enabled" in delta || "show_grid_points" in delta) {
                submitDrawOverlay(sharedConfig.get("overlay_type"), sharedConfig.get("animation_enabled"));
            }
        });

        sharedConfig.on(seq`change:animation_enabled.?`, enabled => {
            if (!enabled) {
                stopCurrentAnimation(true);
            } else {
                startInterpolation();
            }
        });

        // Add event handlers for showing, updating, and removing spotlight details.
        inputController.on(seq`click.?`, saveSpotlight);
        d3.select("#location-close").on(seq`click.?`, removeSpotlight);
        primaryLayerAgent.on(seq`update.?`, updateSpotlight_debounced);
        overlayLayerAgent.on(seq`update.?`, updateSpotlight_debounced);
        sharedConfig.on(seq`change:spotlight_coords.?`, updateSpotlight_debounced);

        // Modify menu depending on what mode we're in.
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.selectAll(".ocean-mode").classed("hidden", mode !== "ocean");
            d3.selectAll(".wind-mode").classed("hidden", mode !== "air")
                .selectAll(".text-button").classed("disabled", mode !== "air");  // hack: hot keys cannot change height
            d3.selectAll(".chem-mode").classed("hidden", mode !== "chem");
            d3.selectAll(".particulates-mode").classed("hidden", mode !== "particulates");
            d3.selectAll(".space-mode").classed("hidden", mode !== "space");
            // d3.selectAll(".argo-mode").classed("hidden", mode !== "argo");
            // d3.select("#notice").classed("hidden", true);
            switch (mode) {
                case "chem":
                case "particulates":
                    //d3.select("#notice").classed("hidden", false);
                    // fall through
                case "air":
                    d3.select("#nav-prev-2").attr("title", "-1 Day");
                    d3.select("#nav-prev-1").attr("title", "-3 Hours");
                    d3.select("#nav-next-1").attr("title", "+3 Hours");
                    d3.select("#nav-next-2").attr("title", "+1 Day");
                    break;
                case "ocean":
                    d3.select("#nav-prev-2").attr("title", "-1 Month");
                    d3.select("#nav-prev-1").attr("title", "-5 Days");
                    d3.select("#nav-next-1").attr("title", "+5 Days");
                    d3.select("#nav-next-2").attr("title", "+1 Month");
                    break;
            }
        });

        // Add handlers for mode buttons.
        d3.select("#air-mode").on(seq`click.?`, function() {
            if (sharedConfig.get("mode") !== "air") {
                sharedConfig.save({
                    mode: "air",
                    animation_type: "wind",
                    z_level: "surface",
                    overlay_type: "wind",
                    animation_enabled: shouldPlayAnimation("air"),
                });
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#air-mode").classed("highlighted", mode === "air");
        });
        d3.select("#ocean-mode").on(seq`click.?`, function() {
            if (sharedConfig.get("mode") !== "ocean") {
                // When switching between modes, there may be no associated data for the current date. So we need
                // find the closest available according to the catalog. This is not necessary if date is "current".
                // UNDONE: this code is annoying. should be easier to get date for closest ocean product.
                const ocean = {
                    mode: "ocean",
                    animation_type: "primary_waves",
                    z_level: "surface",
                    overlay_type: "significant_wave_height",
                    animation_enabled: shouldPlayAnimation("ocean"),
                };
                const attr = sharedConfig.getAll();
                if (attr.time_cursor === "current") {
                    sharedConfig.save(ocean);
                } else {
                    const product = createPrimaryWavesLayer(Object.assign(attr, ocean));
                    if (product.validTime()) {
                        ocean.time_cursor = utc.normalize(product.validTime());
                        sharedConfig.save(ocean);
                    }
                }
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#ocean-mode").classed("highlighted", mode === "ocean");
        });

        d3.select("#chem-mode").on(seq`click.?`, () => {
            if (sharedConfig.get("mode") !== "chem") {
                sharedConfig.save({
                    mode: "chem",
                    animation_type: "wind",
                    z_level: "surface",
                    overlay_type: "cosc",
                    animation_enabled: shouldPlayAnimation("chem"),
                });
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#chem-mode").classed("highlighted", mode === "chem");
        });

        d3.select("#particulates-mode").on(seq`click.?`, function() {
            if (sharedConfig.get("mode") !== "particulates") {
                sharedConfig.save({
                    mode: "particulates",
                    animation_type: "wind",
                    z_level: "surface",
                    overlay_type: "duexttau",
                    animation_enabled: shouldPlayAnimation("particulates"),
                });
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#particulates-mode").classed("highlighted", mode === "particulates");
        });

        d3.select("#space-mode").on(seq`click.?`, function() {
            if (sharedConfig.get("mode") !== "space") {
                sharedConfig.save({
                    mode: "space",
                    animation_type: "wind",
                    z_level: "surface",
                    overlay_type: "aurora",
                    animation_enabled: shouldPlayAnimation("space"),
                });
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#space-mode").classed("highlighted", mode === "space");
        });

/*
        d3.select("#argo-mode").on(seq`click.?`, function() {
            if (sharedConfig.get("mode") !== "argo") {
                sharedConfig.save({
                    mode: "argo",
                    animation_type: "currents",
                    z_level: "2p5dbar",
                    overlay_type: "argo_mean_temp",
                    animation_enabled: shouldPlayAnimation("argo"),
                });
            }
        });
        sharedConfig.on(seq`change:mode.?`, mode => {
            d3.select("#argo-mode").classed("highlighted", mode === "argo");
        });
*/

        // Add logic to disable buttons that are incompatible with each other.
        sharedConfig.on(seq`change:overlay_type.?`, ot => {
            d3.selectAll(".surface").classed("disabled", function() {
                if (sharedConfig.get("mode") !== "air") {
                    return true;
                }
                return this.getAttribute("id") === "surface-level" ?
                    ot === "wind_power_density" :
                    ot === "misery_index";
            });
        });
        sharedConfig.on(seq`change:z_level.?`, z_level => {
            d3.select("#wind_power_density").classed("disabled", z_level === "surface");
        });

        sharedConfig.on(seq`change:time_cursor.?`, time_cursor => {
            calendar.update(time_cursor);
        });

        // Add event handlers for the time navigation buttons.
        d3.select("#nav-prev-2").on(seq`click.?`, () => navigate(activeLayerAgent.value(), "prev2"));
        d3.select("#nav-next-2").on(seq`click.?`, () => navigate(activeLayerAgent.value(), "next2"));
        d3.select("#nav-prev-1").on(seq`click.?`, () => navigate(activeLayerAgent.value(), "prev1"));
        d3.select("#nav-next-1").on(seq`click.?`, () => navigate(activeLayerAgent.value(), "next1"));
        d3.select("#nav-now").on(seq`click.?`, () => sharedConfig.save({time_cursor: "current"}));

        d3.select("#toggle-grid").on(seq`click.?`, () => sharedConfig.toggle("show_grid_points"));
        sharedConfig.on(seq`change:show_grid_points.?`, show_grid_points => {
            d3.select("#toggle-grid").classed("highlighted", show_grid_points);
        });

        d3.select("#toggle-animation").on(seq`click.?`, () => sharedConfig.toggle("animation_enabled"));
        sharedConfig.on(seq`change:animation_enabled.?`, animation_enabled => {
            d3.select("#toggle-animation").classed("highlighted", animation_enabled);
        });

        d3.select("#toggle-hd").on(seq`click.?`, function() {
            sessionConfig.save({hd_enabled: !sessionConfig.get("hd_enabled")});
        });
        sessionConfig.on(seq`change:hd_enabled.?`, hd_enabled => {
            d3.select("#toggle-hd").classed("highlighted", hd_enabled);
        });

        // Add handlers for all wind level buttons.
        d3.selectAll(".surface").each(function() {
            const name = this.dataset.name;
            bindButtonToConfiguration(`[data-name="${name}"]`, {mode: "air", z_level: name});
        });

        // Add handlers for ocean animation types.
        bindButtonToConfiguration(
            "#animate-currents",
            {mode: "ocean", animation_type: "currents", z_level: "surface", animation_enabled: true, overlay_type: "currents"},
            [{mode: "ocean", animation_type: "currents", z_level: "surface"}]);
        bindButtonToConfiguration(
            "#animate-waves",
            {mode: "ocean", animation_type: "primary_waves", z_level: "surface", animation_enabled: true, overlay_type: "primary_waves"},
            [{mode: "ocean", animation_type: "primary_waves", z_level: "surface"}]);

/*
        // Add handlers for all argo depth buttons.
        d3.selectAll(".argo-surface").each(function() {
            const name = this.dataset.name;
            bindButtonToConfiguration(`[data-name="${name}"]`, {mode: "argo", z_level: name});
        });
*/

        // Add handlers for all overlay buttons.
        overlayTypes.forEach(function(type) {
            const newAttr = {overlay_type: type}, highlightAttrList = [newAttr];
            if (type === "misery_index") {
                Object.assign(newAttr, {z_level: "surface"});
            } else if (type === "currents") {
                highlightAttrList.push({mode: "ocean", animation_type: "currents", z_level: "surface", overlay_type: "currents"});
            } else if (type === "primary_waves") {
                highlightAttrList.push({mode: "ocean", animation_type: "primary_waves", z_level: "surface", overlay_type: "primary_waves"});
            }
            bindButtonToConfiguration(`#${type.replace(".", "")}`, newAttr, highlightAttrList);
        });
        bindButtonToConfiguration("#wind", {mode: "air", overlay_type: "wind"});
        bindButtonToConfiguration("#no-overlay", {overlay_type: "none"});

/*
        bindButtonToConfiguration("#argo_temp_mean", {overlay_type: "argo_temp_mean"});
        bindButtonToConfiguration("#argo_temp_anom", {overlay_type: "argo_temp_anom"});
        bindButtonToConfiguration("#argo_salinity_mean", {overlay_type: "argo_salinity_mean"});
        bindButtonToConfiguration("#argo_salinity_anom", {overlay_type: "argo_salinity_anom"});
        bindButtonToConfiguration("#argo-planned", {argoFloat: "planned"});
        bindButtonToConfiguration("#argo-recent", {argoFloat: "recent"});
        bindButtonToConfiguration("#argo-operational", {argoFloat: "operational"});
        bindButtonToConfiguration("#argo-dead", {argoFloat: "dead"});
        bindButtonToConfiguration("#argo-none", {argoFloat: null});
*/

        // Add handlers for all projection buttons.
        Object.keys(globes).forEach(function(p) {
            bindButtonToConfiguration("#" + p, {projection_type: p, orientation: undefined}, [{projection_type: p}]);
        });

        if (isKioskContext()) {
            d3.selectAll(".kiosk").classed("hidden", true);
        } else {
            setTimeout(function() {
                d3.select("#menu-ham").transition().style("opacity", "0").remove();
            }, 15*1000);
        }

        function setupWebGL() {
            const glReport = glTest.runTest(isDebugContext());
            const msg = glReport.pass ? "ok" : JSON.stringify(glReport);
            console.log(`test gl: ${msg}`);
            gtagEvent("report", {category: "gl", message: msg});
            if (!glReport.pass) {
                return;
            }

            function ƒready() {
                return sharedConfig.get("overlay_type") !== "none" && rendererAgent.value();
            }
            function ƒlayer() {
                return activeLayerAgent.value();
            }
            function ƒalpha() {
                const layer = ƒlayer();
                const alpha = sharedConfig.get("animation_enabled") ? layer?.alpha.animated : layer?.alpha.single;
                return (alpha ?? 0) / 255;
            }
            function ƒdisplay() {
                const {width, height} = viewboxAgent.value();
                return {width, height, pixelRatio: sessionConfig.get("hd_enabled") ? 1.0 : 0.5};
            }
            function ƒprojection(glu) {
                return globeAgent.value()?.projection.optimize().webgl?.(glu);
            }
            function ƒgrid(glu) {
                return ƒlayer()?.grid?.().webgl?.(glu);
            }
            function ƒfield(glu) {
                const {interpolation_type} = sessionConfig.getAll();
                return ƒlayer()?.field?.()[interpolation_type]?.webgl?.(glu);
            }
            function ƒscale(glu) {
                return ƒlayer()?.scale.webgl?.(glu);
            }
            /**
             * Get the webgl components that are currently renderable.
             *
             * @param glu
             * @returns {Object[]} an array of webgl components, empty if any component does not support webgl.
             */
            function ƒcomponents(glu) {
                if (!ƒready()) {
                    return [];
                }
                const components = [
                    ƒprojection(glu),
                    ƒgrid(glu),
                    ƒfield(glu),
                    ƒscale(glu)
                ].filter(e => !!e);
                return components.length === 4 ? components : [];
            }

            const {width, height} = viewboxAgent.value();
            const canvas = d3.select("#fastoverlay")
                .style("width", `${width}px`)
                .style("height", `${height}px`)
                .node();
            const fastoverlay = buildFastoverlay(
                canvas,
                isIOS() ? twod.createCanvas() : undefined,
                ƒalpha,
                ƒdisplay,
                ƒcomponents,
            );

            // All of these events trigger a new frame.
            sharedConfig.on(seq`change:overlay_type.?`, fastoverlay.draw);
            sessionConfig.on(seq`change:hd_enabled.?`, fastoverlay.draw);
            globeAgent.on(seq`update.?`, fastoverlay.draw);
            activeLayerAgent.on(seq`update.?`, fastoverlay.draw);
            rendererAgent.on(seq`redraw.?`, fastoverlay.draw);
            rendererAgent.on(seq`render.?`, fastoverlay.draw);
            animatorAgent.on(seq`update.?`, fastoverlay.draw);

            fastoverlayAgent.submit(fastoverlay);
        }

        setupWebGL();

        d3.select("body")
            .on(seq`keydown.?`, handleAsChord(interpretKeyDown))
            .on(seq`keypress.?`, handleAsChord(interpretKeyPress));
    }

    const pressure = [
        "surface",
        "1000hPa",
        "850hPa",
        "700hPa",
        "500hPa",
        "250hPa",
        "70hPa",
        "10hPa"
    ];

    function pressureLevel() {
        const attr = sharedConfig.getAll();
        return attr.mode === "air" ? pressure.indexOf(attr.z_level) : -1;
    }

    function clearDialog() {
        if (!d3.select("#settings-wrap").classed("hidden")) {
            doClick("#settings-hide");
            return true;
        }
        if (!d3.select("#calendar-wrapper").classed("hidden")) {
            doClick("#calendar-hide");
            return true;
        }
        if (!d3.select("#notice").classed("hidden")) {
            doClick("#notice-hide");
            return true;
        }
        if (!d3.select("#location-close").classed("hidden")) {
            doClick("#location-close");
            return true;
        }
        if (!d3.select("#menu").classed("collapsed")) {
            doClick("#toggle-menu");
            return true;
        }
        if (!d3.select("#sponsor").classed("hidden")) {
            doClick("#sponsor-hide");
            return true;
        }
    }

    function applyCalendar() {
        // UNDONE: allow Enter key to select years/months/days. Also, Enter triggers click on certain elements.
        if (!d3.select("#calendar-wrapper").classed("hidden")) {
            doClick("#calendar-apply");
            return true;
        }
    }

    function interpretKeyDown(chord) {
        switch (chord) {
            case "Escape": return clearDialog();
        }
    }

    function interpretKeyPress(chord) {
        switch (chord) {
            case "e": return doClick("#toggle-menu");
            case "I": return doClick(`[data-name="${pressure[pressure.length - 1]}"]`);
            case "i": return doClick(`[data-name="${pressure[pressureLevel() + 1]}"]`);
            case "m": return doClick(`[data-name="${pressure[pressureLevel() - 1]}"]`);
            case "M": return doClick(`[data-name="${pressure[0]}"]`);
            case "J": return doClick("#nav-prev-2");
            case "j": return doClick("#nav-prev-1");
            case "k": return doClick("#nav-next-1");
            case "K": return doClick("#nav-next-2");
            case "n": return doClick("#nav-now");
            case "p": return doClick("#toggle-animation");
            case "g": return doClick("#toggle-grid");
            case "H": return doClick("#toggle-hd");
            case "C": return doClick("#toggle-calendar");
            case "d": return inputController.hotkey("zoom", 1.003);
            case "x": return inputController.hotkey("zoom", 0.997);
            case "D": return inputController.hotkey("zoom", 1.05);
            case "X": return inputController.hotkey("zoom", 0.95);
            case "w": return inputController.hotkey("up", 1);
            case "W": return inputController.hotkey("up", 8);
            case "z": return inputController.hotkey("down", 1);
            case "Z": return inputController.hotkey("down", 8);
            case "a": return inputController.hotkey("left", 1);
            case "A": return inputController.hotkey("left", 8);
            case "s": return inputController.hotkey("right", 1);
            case "S": return inputController.hotkey("right", 8);
            case "Enter": return applyCalendar();
            // Never bind to keys used for search in various browsers:  /.',
            // Never bind to cursor keys (used for scrolling during zoom), or to Alt|Cmd|Ctrl chords.
            // Never bind to these keys: http://stackoverflow.com/questions/9612458/hot-keys-to-avoid
        }
    }

    function notifyIE() {
        d3.select("#notice").property("hidden", !isIE11());
    }

    function showDetails() {
        d3.select("body").style("background", "#505050");
        d3.select("#display")
            .append("div")
            .style("position", "absolute")
            .style("top", "20%")
            .style("left", 0)
            .style("font-size", "1.5rem")
            .style("word-break", "break-all")
            .append("p").text("ref: " + document.referrer)
            .append("p").text("loc: " + document.location)
            .append("p").text("ua: " + navigator.userAgent);
    }

    function printDetails() {
        let count = 0;
        d3.select("#langs").on(seq`click.?`, () => {
            if (++count === 2) {
                showDetails();
                d3.event.preventDefault();
            }
        });

        let task2;
        let count2 = 0;
        sharedConfig.on(seq`change:animation_enabled.?`, () => {
            count2++;
            if (count2 === 20) {
                showDetails();
            }
            clearTimeout(task2);
            task2 = setTimeout(() => count2 = 0, 1000);
        });
    }

    function start() {
        // Everything is now set up, so load state from the hash fragment and kick off change events.
        sharedAttach();
        sessionAttach();
        notifyIE();
        printDetails();
    }

    Promise.resolve().then(init).then(start).catch(report.error).then(() => bridge.ready?.());

    /**
     * Triggers a click event on the specified button.
     *
     * @param {String} selector D3 selection string for the button to click
     * @returns {boolean} true
     */
    function doClick(selector) {
        defer(() => d3.select(selector).node()?.dispatchEvent(newClickEvent()));
        return true;
    }

    return {

        url() {
            return window.location.href.toString();
        },

        scale: {

            /**
             * @param {Number} pct percentage in the range [0, 100] (inclusive)
             * @returns {String} JSON encoded map of units to values, or "{}" if no product is active. For example:
             *     {
             *         "wind": {
             *             "m/s" : "100° @ 7.3",
             *             "kn"  : "100° @ 14",
             *             "mph" : "100° @ 16"
             *         }
             *     }
             */
            valueAt(pct) {
                const layer = activeLayerAgent.value(), result = {};
                if (layer) {
                    const group = {}, value = rescale(pct, 0, 100, ...layer.scale.bounds);
                    Object.values(layer.unitDescriptors).forEach(unit => {
                        group[unit.symbol] = unit.format(value).formattedVal;
                    });
                    result[layer.type] = group;
                }
                return JSON.stringify(result);
            },

            /**
             * @returns {String} a JSON encoded array of colors, "[r0, g0, b0, a0, ..., rn-1, gn-1, bn-1, an-1]",
             *          where n is the size of the palette. The result is empty "{}" if no product is active, otherwise
             *          the length will be size*4.
             *          {
             *              "wind": [30, 20, 92, 255, ..., 193, 245, 223, 255]
             *          }
             */
            palette() {
                const layer = activeLayerAgent.value();
                return JSON.stringify(layer ? {[layer.type]: toArray(layer.scale.colors)} : {});
            }
        },

        click: doClick,

        /**
         * Shows spotlight marker at specified coordinates.
         *
         * @param {Number} lon
         * @param {Number} lat
         */
        locate(lon, lat) {
            // side effects means we need to defer
            defer(() => moveSpotlightTo(lon, lat));
        },

        orient(coord) {  // HACK for now. What's the best way to do this?
            defer(() => {
                const globe = globeAgent.value();
                if (coord === undefined) {
                    return globe.projection.rotate();
                }
                globe.projection.rotate(coord);
                sharedConfig.save({orientation: globe.getOrientation()});  // triggers reorientation
            });
        },

        /**
         * Stop animation.
         */
        stop() {
            // side effects means we need to defer
            defer(() => sharedConfig.save({animation_enabled: false}));
        },

        /**
         * Forcefully ends the current move operation, if any.
         */
        cancelMove() {
            inputController.cancelMove();
        },

    };
}
